import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert, Label, Input, FormGroup
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const KullaniciSertifikalari = () => {
  document.title = "Kullanıcı Sertifikaları | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [sertifikalar, setSertifikalar] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [sertifikaTanimlari, setSertifikaTanimlari] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    KULLANICI_ID: "",
    SERTIFIKA_ID: "",
    ATANMA_TARIHI: new Date().toISOString().split("T")[0],
    DURUM: "Aktif"
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [sertResp, kulResp, tanimResp] = await Promise.all([
        // KULLANICI_SERTIFIKALARI list — SERTIFIKA_TANIMLARI'nda sadece ID/AD/AKTIF var, ACIKLAMA yok.
        fetch(`${BASE_URL}/KULLANICI_SERTIFIKALARI?$top=500&$orderby=ID desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress),SERTIFIKA_TANIMLARI($select=AD)`),
        // KULLANICILAR.AKTIF_DURUM string ('Aktif'/'Pasif'), boolean değil. Admin tüm kullanıcıları seçim için görsün.
        fetch(`${BASE_URL}/KULLANICILAR?$select=ID,AD_SOYAD,Mail_Adress,HESAP_TURU&$top=500&$orderby=AD_SOYAD asc`),
        fetch(`${BASE_URL}/SERTIFIKA_TANIMLARI?$select=ID,AD&$top=200`)
      ]);

      const [sertResult, kulResult, tanimResult] = await Promise.all([
        sertResp.json(),
        kulResp.json(),
        tanimResp.json()
      ]);

      if (sertResult?.value) setSertifikalar(sertResult.value);
      if (kulResult?.value) setKullanicilar(kulResult.value);
      if (tanimResult?.value) setSertifikaTanimlari(tanimResult.value);
    } catch (err) {
      setError("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = async () => {
    if (!formData.KULLANICI_ID || !formData.SERTIFIKA_ID) {
      toast.warning("Kullanıcı ve sertifika seçimi zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        KULLANICI_ID: parseInt(formData.KULLANICI_ID),
        SERTIFIKA_ID: parseInt(formData.SERTIFIKA_ID),
        ATANMA_TARIHI: new Date(formData.ATANMA_TARIHI).toISOString(),
        DURUM: formData.DURUM
      };
      const resp = await fetch(`${BASE_URL}/KULLANICI_SERTIFIKALARI`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload)
      });
      if (!resp.ok) throw new Error();
      toast.success("Sertifika başarıyla atandı.");
      setAddModal(false);
      resetForm();
      fetchData();
    } catch {
      toast.error("Sertifika atanırken hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDurumToggle = async (item) => {
    const yeniDurum = item.DURUM === "Aktif" ? "Pasif" : "Aktif";
    try {
      await fetch(`${BASE_URL}/KULLANICI_SERTIFIKALARI(${item.ID})`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ DURUM: yeniDurum })
      });
      toast.success(`Sertifika durumu "${yeniDurum}" olarak güncellendi.`);
      fetchData();
    } catch {
      toast.error("Durum güncellenirken hata oluştu.");
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await fetch(`${BASE_URL}/KULLANICI_SERTIFIKALARI(${selectedItem.ID})`, {
        method: "DELETE",
        headers
      });
      toast.success("Sertifika kaydı silindi.");
      setDeleteModal(false);
      fetchData();
    } catch {
      toast.error("Silme işlemi sırasında hata oluştu.");
    }
  };

  const resetForm = () => {
    setFormData({
      KULLANICI_ID: "",
      SERTIFIKA_ID: "",
      ATANMA_TARIHI: new Date().toISOString().split("T")[0],
      DURUM: "Aktif"
    });
  };

  const aktifCount = useMemo(() => sertifikalar.filter(s => s.DURUM === "Aktif").length, [sertifikalar]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => <span className="fw-bold">#{c.getValue()}</span>
    },
    {
      header: "Kullanıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => {
        const u = c.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">-</span>;
      }
    },
    {
      header: "Sertifika",
      accessorKey: "SERTIFIKA_TANIMLARI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => {
        const s = c.getValue();
        return s ? (
          <div className="fw-medium">{s.AD}</div>
        ) : <span className="text-muted">-</span>;
      }
    },
    {
      header: "Atanma Tarihi",
      accessorKey: "ATANMA_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => c.getValue() ? new Date(c.getValue()).toLocaleDateString("tr-TR") : "-"
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => (
        <Badge color={c.getValue() === "Aktif" ? "success" : "secondary"}>
          {c.getValue() || "Bilinmiyor"}
        </Badge>
      )
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: cellProps => {
        const item = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button
              color={item.DURUM === "Aktif" ? "warning" : "success"}
              size="sm"
              outline
              onClick={() => handleDurumToggle(item)}
            >
              {item.DURUM === "Aktif" ? "Pasif Yap" : "Aktif Yap"}
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={() => { setSelectedItem(item); setDeleteModal(true); }}
            >
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      }
    }
  ], [sertifikalar]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Kullanıcı Sertifikaları" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row className="mb-3">
            <Col sm="6">
              <div className="d-flex gap-3">
                <Card className="mini-stats-wid shadow-sm border-0 mb-0">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bx bx-check-shield fs-4 text-success" />
                      <div>
                        <p className="text-muted mb-0 small">Aktif Sertifika</p>
                        <h5 className="mb-0">{aktifCount}</h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card className="mini-stats-wid shadow-sm border-0 mb-0">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bx bx-list-ul fs-4 text-primary" />
                      <div>
                        <p className="text-muted mb-0 small">Toplam Kayıt</p>
                        <h5 className="mb-0">{sertifikalar.length}</h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </Col>
            <Col sm="6" className="text-end">
              <Button color="primary" onClick={() => { resetForm(); setAddModal(true); }}>
                <i className="bx bx-plus me-1" />
                Sertifika Ata
              </Button>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Kullanıcı Sertifika Listesi</h4>
                    <Button color="primary" size="sm" outline onClick={fetchData} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={sertifikalar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Kullanıcı veya sertifika ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Sertifika Ata Modal */}
      <Modal isOpen={addModal} toggle={() => setAddModal(false)} centered>
        <ModalHeader toggle={() => setAddModal(false)}>Kullanıcıya Sertifika Ata</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Kullanıcı *</Label>
            <Input
              type="select"
              value={formData.KULLANICI_ID}
              onChange={e => setFormData(prev => ({ ...prev, KULLANICI_ID: e.target.value }))}
            >
              <option value="">-- Kullanıcı Seçin --</option>
              {kullanicilar.map(u => (
                <option key={u.ID} value={u.ID}>
                  {u.AD_SOYAD} ({u.Mail_Adress || u.HESAP_TURU})
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Sertifika *</Label>
            <Input
              type="select"
              value={formData.SERTIFIKA_ID}
              onChange={e => setFormData(prev => ({ ...prev, SERTIFIKA_ID: e.target.value }))}
            >
              <option value="">-- Sertifika Seçin --</option>
              {sertifikaTanimlari.map(s => (
                <option key={s.ID} value={s.ID}>{s.AD}</option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Atanma Tarihi</Label>
            <Input
              type="date"
              value={formData.ATANMA_TARIHI}
              onChange={e => setFormData(prev => ({ ...prev, ATANMA_TARIHI: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Durum</Label>
            <Input
              type="select"
              value={formData.DURUM}
              onChange={e => setFormData(prev => ({ ...prev, DURUM: e.target.value }))}
            >
              <option value="Aktif">Aktif</option>
              <option value="Pasif">Pasif</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setAddModal(false)}>İptal</Button>
          <Button color="primary" onClick={handleAdd} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : "Ata"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Silme Onay Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)}>Sertifika Kaydını Sil</ModalHeader>
        <ModalBody>
          {selectedItem && (
            <p>
              <strong>{selectedItem.KULLANICILAR?.AD_SOYAD}</strong> kullanıcısına ait{" "}
              <strong>{selectedItem.SERTIFIKA_TANIMLARI?.AD}</strong> sertifika kaydını silmek istediğinize emin misiniz?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteModal(false)}>İptal</Button>
          <Button color="danger" onClick={handleDelete}>Sil</Button>
        </ModalFooter>
      </Modal>

      <ToastContainer autoClose={2000} />
    </React.Fragment>
  );
};

export default KullaniciSertifikalari;
